import { useCallback } from "react";

import { ZodError } from "zod";
import { FieldValues } from "react-hook-form";

import mockSignDocument from "./mock-sign-document.json";
import { fetchData } from "../api/apiService";
import { NextVisit, Status, SubmissionInfo } from "../components/types";
import { useAppErrorContext } from "../contexts/AppErrorContext";
import { UNEXPECTED_ERROR_MSG } from "../components/stringConstants";
import { usePatientDataContext } from "../contexts/PatientDataContext";
import { DocumentRetrievalState } from "../components/LoopDocumentRetriever/LoopDocumentRetriever";
import {
  checkIsSubmitDocumentErrorResponse,
  SubmitDocumentResponseSchema,
} from "./SubmitDocumentResponse";
import {
  checkIsGetDocumentErrorResponse,
  checkIsGetLastDocumentSuccessResponse,
  GetDocumentResponseSchema,
  GetDocumentSuccessResponse,
} from "./GetDocumentResponse";
import { DocumentSuccessResponse } from "./DocumentSuccessResponse";

enum GetDocumentStatus {
  Success,
  LastDocument,
  Error,
}

export function useDocumentFetchingInfrastructure(
  setDocumentRetrievalState: React.Dispatch<
    React.SetStateAction<DocumentRetrievalState>
  >,
  setResponseDocument: React.Dispatch<React.SetStateAction<any>>,
  displaySubmissionInfo: (info: SubmissionInfo | null) => void,
  contractIdOverride?: number | string
) {
  const {
    sessionId,
    verificationCodes,
    patientId,
    contractId,
    facilityId,
    viewedContractIds,
  } = usePatientDataContext();
  const { openErrorDialog } = useAppErrorContext();

  // The questionnaire step clears the contract from the context once it is
  // submitted, so the contract the server named in its response wins.
  const activeContractId = contractIdOverride ?? contractId;

  const fetchDocument = useCallback(async () => {
    try {
      const responseData = await fetchData("POST", `/npp/get-document`, {
        sessionId: sessionId,
        verificationCode: verificationCodes,
        pid: patientId,
        contractId: activeContractId,
        facilityId: facilityId,
        viewedContracts: viewedContractIds,
      });
      return responseData;
    } catch (error) {
      throw error;
    }
  }, [
    sessionId,
    verificationCodes,
    patientId,
    activeContractId,
    facilityId,
    viewedContractIds,
  ]);

  const handleErrorRetrieveResponse = useCallback(
    (errorMessage: string) => {
      openErrorDialog(errorMessage);
      setDocumentRetrievalState(DocumentRetrievalState.Retry);
    },
    [openErrorDialog, setDocumentRetrievalState]
  );

  const handleLastSuccessfulDocumentSent = useCallback(
    async (successMessage: string, nextVisit?: NextVisit) => {
      setResponseDocument({
        isLastDocument: true,
        successMessage,
        nextVisit,
      });

      displaySubmissionInfo({
        mainMessage: successMessage,
        status: Status.success,
      });
    },
    [displaySubmissionInfo, setResponseDocument]
  );

  const handleSuccessfulDocumentResponse = useCallback(
    (documentResponse: GetDocumentSuccessResponse) => {
      const docResponse = constructResponseDocument(documentResponse);
      setResponseDocument(docResponse);
      setDocumentRetrievalState(DocumentRetrievalState.Document);
    },
    [setResponseDocument, setDocumentRetrievalState]
  );

  const getDocument = useCallback(async () => {
    window.scrollTo(0, 0);
    let responseData: any = {};
    setDocumentRetrievalState(DocumentRetrievalState.Loading);
    setResponseDocument(null);
    try {
      responseData = await fetchDocument();
    } catch (error) {
      handleErrorRetrieveResponse(UNEXPECTED_ERROR_MSG);
      return GetDocumentStatus.Error;
    }
    const { error, data: validData } =
      GetDocumentResponseSchema.safeParse(responseData);

    if (error) {
      logIncorrectResponse(error, responseData);
      handleErrorRetrieveResponse(UNEXPECTED_ERROR_MSG);
      return GetDocumentStatus.Error;
    }

    if (checkIsGetDocumentErrorResponse(validData)) {
      handleErrorRetrieveResponse(validData.data.errorMessage);
      return GetDocumentStatus.Error;
    }

    if (checkIsGetLastDocumentSuccessResponse(validData)) {
      await handleLastSuccessfulDocumentSent(
        validData.data.successMessage,
        validData.data.nextVisit
      );
      return GetDocumentStatus.LastDocument;
    }

    handleSuccessfulDocumentResponse(validData);
    return GetDocumentStatus.Success;
  }, [
    fetchDocument,
    handleErrorRetrieveResponse,
    handleLastSuccessfulDocumentSent,
    handleSuccessfulDocumentResponse,
    setDocumentRetrievalState,
    setResponseDocument,
  ]);

  const handleErrorSubmitResponse = useCallback(
    (errorMessage: string) => {
      displaySubmissionInfo({
        mainMessage: errorMessage,
        status: Status.error,
      });
    },
    [displaySubmissionInfo]
  );

  const postDocument = useCallback(
    async (formData: FieldValues, documentId: number) => {
      setDocumentRetrievalState(DocumentRetrievalState.Loading);
      let response = null;
      try {
        response = await fetchData("POST", `/npp/sign-document`, {
          documentId: documentId,
          pid: patientId,
          contractId: activeContractId,
          facilityId: facilityId,
          sessionId: sessionId,
          verificationCode: verificationCodes,
          formData: { ...formData },
        });
      } catch (error) {
        openErrorDialog(UNEXPECTED_ERROR_MSG);
        setDocumentRetrievalState(DocumentRetrievalState.Document);
        return
      }
      const submitData = response;
      const { error, data: validData } =
        SubmitDocumentResponseSchema.safeParse(submitData);

      if (error) {
        logIncorrectResponse(error, submitData);
        handleErrorSubmitResponse(UNEXPECTED_ERROR_MSG);
        return;
      }
      if (checkIsSubmitDocumentErrorResponse(validData)) {
        handleErrorSubmitResponse(validData.data.errorMessage);
        return;
      }
      const status = await getDocument();
      if (status === GetDocumentStatus.Success) {
        displaySubmissionInfo({
          mainMessage: validData.data.successMessage,
          status: Status.success,
        });
      }
    },
    [
      patientId,
      activeContractId,
      facilityId,
      sessionId,
      verificationCodes,
      setDocumentRetrievalState,
      getDocument,
      handleErrorSubmitResponse,
      displaySubmissionInfo,
      openErrorDialog,
    ]
  );
  return { getDocument, postDocument };
}

export function constructResponseDocument(
  response: GetDocumentSuccessResponse
): DocumentSuccessResponse {
  return {
    id: Number(response.data.document.id),
    documentHTML: response.data.document.html,
    title: response.data.document.title,
    jsonData: response.data.jsonData,
  };
}

export const logIncorrectResponse = (
  error: ZodError,
  responseData: unknown
) => {
  console.error("Error parsing response:", error);
  console.error("responseData", responseData);
};
